import {createMuiTheme} from '@material-ui/core/styles'
import animations from './animations'
import icons from './icons'
import {createSizes} from './sizes'

const colors = {
  amber200: '#FFE082',
  amber50: '#FFF8E1',
  amber700: '#FFA000',
  blueGrey700: '#455A64',
  blueGrey900: '#263238',
  cyan500: '#00BCD4',
  deepOrange50: '#FBE9E7',
  deepOrange500: '#FF5722',
  deepOrange700: '#E64A19',
  green100: '#C8E6C9',
  green50: '#E8F5E9',
  green500: '#4CAF50',
  green700: '#388E3C',
  grey400: '#BDBDBD',
  grey500: '#9E9E9E',
  grey600: '#757575',
  grey700: '#616161',
  grey800: '#424242',
  grey900: '#212121',
  indigo100: '#C5CAE9',
  indigo50: '#E8EAF6',
  indigo700: '#303F9F',
  lightBlue700: '#0288D1',
  pink500: '#E91E63',
  purple700: '#7B1FA2',
  red100: '#FFCDD2',
  red50: '#FFEBEE',
  red700: '#D32F2F',
}

const minViewWidth = 1024
const maxViewWidth = 1400

const muiV1Theme = createMuiTheme({
  overrides: {
    MuiDivider: {
      root: {
        backgroundColor: 'var(--cg-color-border-light)',
      },
    },
    MuiPaper: {
      root: {
        backgroundColor: 'var(--cg-color-bg-white)',
        color: 'var(--cg-color-text-primary)',
      },
    },
    MuiDialogActions: {
      root: {
        backgroundColor: 'var(--cg-color-bg-white)',
      },
    },
    MuiDialogTitle: {
      root: {
        color: 'var(--cg-color-text-primary)',
        '& h2': {
          color: 'var(--cg-color-text-primary)',
        },
      },
    },
    MuiStepLabel: {
      label: {
        color: 'var(--cg-color-text-secondary)',
        '&$active': {
          color: 'var(--cg-color-text-primary)',
        },
        '&$completed': {
          color: 'var(--cg-color-text-primary)',
        },
      },
    },
    MuiStepIcon: {
      text: {
        fill: '#FFFFFF',
      },
    },
    MuiTypography: {
      root: {
        color: 'var(--cg-color-text-primary)',
      },
      body1: {
        color: 'var(--cg-color-text-primary)',
      },
      body2: {
        color: 'var(--cg-color-text-primary)',
      },
      subheading: {
        color: 'var(--cg-color-text-primary)',
      },
      colorTextSecondary: {
        color: 'var(--cg-color-text-secondary)',
      },
    },
    MuiInput: {
      root: {
        color: 'var(--cg-color-text-primary)',
      },
      multiline: {
        padding: '12px 0 12px',
      },
    },
    MuiInputLabel: {
      root: {
        color: 'var(--cg-color-text-secondary)',
      },
    },
    MuiMenuItem: {
      root: {
        color: 'var(--cg-color-text-primary)',
      },
    },
    MuiListItemIcon: {
      root: {
        marginRight: '24px !important',
      },
    },
    MuiFormControlLabel: {
      label: {
        fontWeight: 300,
      },
    },
    MuiCheckbox: {
      // v1 disables pointer-events on a disabled Checkbox (via MuiButtonBase),
      // which removes the cursor affordance v0 had. Restore the not-allowed
      // cursor globally; clicks stay inert because the input is still disabled.
      root: {
        // Restore v0's darker unchecked box; v1 defaults it to a lighter grey.
        // Checked/disabled states out-specify this, so only the empty box changes.
        color: 'var(--cg-color-text-primary)',
        '&$disabled': {
          cursor: 'not-allowed',
          pointerEvents: 'auto',
        },
      },
    },
    MuiSwitch: {
      bar: {
        backgroundColor: 'var(--cg-color-border-medium)',
        opacity: 1,
      },
    },
  },
  palette: {
    primary: {
      main: colors.deepOrange700,
      light: colors.deepOrange50,
      dark: colors.deepOrange500,
    },
    secondary: {
      main: colors.deepOrange700,
      light: colors.deepOrange50,
      dark: colors.deepOrange500,
    },
    error: {
      main: colors.red700,
      light: colors.red50,
      dark: colors.red700,
    },
    text: {
      primary: colors.blueGrey900,
      secondary: '#888888',
      disabled: '#AAAAAA',
    },
    background: {
      default: '#f2f2f2',
      paper: '#FFFFFF',
    },

    headerColor: colors.blueGrey900,

    primary1HoverBackgroundColor: 'rgba(255,87,34,0.15)',

    borderColor: '#CFD4D8',
    lightBorderColor: '#EEE',

    background1Color: '#f2f2f2',
    background2Color: '#eeeeee',
    background3Color: '#e8e8e8',
    background4Color: '#d8d8d8',
    disabledBackgroundColor: '#F2F2F2',
    hoverBackgroundColor: '#EEEEEE',
    hoverHighlightColor: colors.blueGrey700,

    primaryFontColor: colors.blueGrey900,
    darkTextColor: colors.blueGrey900,
    lightTextColor: '#888888',
    disabledColor: '#AAAAAA',
    disabledTextColor: '#AAAAAA',

    primaryIconColor: colors.blueGrey900,
    lightIconColor: '#999999',
    disabledIconColor: '#AAAAAA',

    proPurple: '#58276f',
    proPurpleBackground: 'rgba(104, 31, 128, 0.4)',
    proPink: '#e61a65',
    proPinkBackground: 'rgba(238, 27, 111, 0.4)',

    contrastBackgroundColor: colors.grey800,
    contrastBorderColor: colors.grey900,
    contrastTextColor: colors.grey400,
    contrastIconColor: colors.grey400,
    contrastHoverBackgroundColor: colors.grey600,
    contrastHoverTextColor: colors.grey400,
    contrastActiveBackgroundColor: '#fff',
    contrastActiveTextColor: '#58276f',
    contrastActiveIconColor: '#58276f',

    goodColor: colors.green500,
    goodBackgroundColor: colors.green50,
    goodBackground2Color: colors.green100,

    infoColor: colors.indigo700,
    infoBackgroundColor: colors.indigo50,
    infoBackground2Color: colors.indigo100,

    errorColor: colors.red700,
    errorBackgroundColor: colors.red50,
    errorBackground2Color: colors.red100,

    warningColor: colors.amber700,
    warningBackgroundColor: colors.amber50,
    warningBorderColor: colors.amber200,

    whiteColor: '#FFFFFF',
    redColor: colors.red700,
    blackColor: '#020202',

    signatureColor: 'black',

    contacts: {
      postedStatus: {
        notPosted: colors.grey500,
        posted: colors.green700,
        failedToPost: colors.red700,
      },
      emailSendStatus: {
        notSent: colors.grey500,
        ok: colors.green700,
        softBounce: colors.amber700,
        hardBounce: colors.red700,
      },
      emailValidation: {
        valid: colors.green700,
        invalid: colors.red700,
      },
    },

    deliveryNoteStatus: {
      draft: colors.grey500,
      draftReturn: colors.red700,
      awaitingReturn: colors.red700,
      returned: colors.red700,
      scheduled: colors.amber700,
      delivered: colors.green700,
      collected: colors.green700,
    },
    deliveryItemStatus: {
      pending: colors.grey500,
      readyForDelivery: colors.green700,
      scheduled: colors.amber700,
      delivered: colors.lightBlue700,
    },
    deliveryStatus: {
      unscheduled: colors.grey500,
      scheduled: colors.indigo700,
      wip: colors.amber700,
      onHold: colors.red700,
      complete: colors.green700,
    },
    jobCardStatus: {
      pending: colors.indigo700,
      wip: colors.amber700,
      onHold: colors.red700,
      complete: colors.green700,
      notScheduled: colors.grey500,
      noProductionRequired: colors.green700,
      delivered: colors.lightBlue700,
    },
    opportunityStatus: {
      rejected: colors.red700,
      won: colors.green700,
      open: colors.amber700,
    },
    productionStatus: {
      unscheduled: colors.grey500,
      scheduled: colors.indigo700,
      wip: colors.amber700,
      onHold: colors.red700,
      complete: colors.green700,
    },
    quoteStatus: {
      draft: colors.grey500,
      quoted: colors.indigo700,
      accepted: colors.cyan500,  // pseudo-status
      confirmed: colors.amber700,
      partDelivered: colors.lightBlue700,
      delivered: colors.lightBlue700,
      inDelivery: colors.lightBlue700,
      invoiced: colors.purple700,
      credited: colors.pink500,
      paid: colors.green700,
      closed: colors.grey700,
      rejected: colors.red700,
      noSchedule: colors.grey500,
    },
    approvalStatus: {
      approved: colors.green700,
      changesRequested: colors.purple700,
      draft: colors.grey500,
      inReview: colors.amber700,
      rejected: colors.red700,
    },
    purchaseOrderStatus: {
      draft: colors.grey500,
      issued: colors.indigo700,
      partReceived: colors.lightBlue700,
      received: colors.lightBlue700,
      closed: colors.grey700,
      cancelled: colors.red700,
      confirmed: colors.amber700,
      paid: colors.green700,
    },
    invoiceType: {
      awaitingPosting: colors.grey500,
      invoice: colors.green500,
      creditNote: colors.purple700,
    },

    pendingStatusColor: colors.indigo700,
    inProgressStatusColor: colors.amber700,
    onHoldStatusColor: colors.red700,
    rejectStatusColor: colors.red700,
    completedStatusColor: colors.green700,
  },

  typography: {
    useNextVariants: true,
    h1: {
      fontSize: 112,
      fontWeight: 300,
      lineHeight: '128px',
      letterSpacing: -5,
    },
    h2: {
      fontSize: 56,
      fontWeight: 400,
      lineHeight: '64px',
    },
    h3: {
      fontSize: 45,
      fontWeight: 400,
      lineHeight: '64px',
    },
    h4: {
      fontSize: 34,
      fontWeight: 400,
      lineHeight: '52px',
    },
    h5: {
      fontSize: 24,
      fontWeight: 400,
      lineHeight: '44px',
    },
    h6: {
      fontSize: 20,
      fontWeight: 600,
      lineHeight: '44px',
    },
    subtitle1: {
      fontSize: 16,
      fontWeight: 400,
      lineHeight: '32px',
    },
    body1: {
      fontSize: 14,
      fontWeight: 400,
      lineHeight: '25px',
    },
    body2: {
      fontSize: 14,
      fontWeight: 400,
      lineHeight: '25px',
    },
    button: {
      fontSize: 14,
      textTransform: 'uppercase',
      fontWeight: 600,
      lineHeight: '32px',
    },
    caption: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: '32px',
    },
  },

  partStatus: {
    active: colors.green700,
    buyOnly: colors.indigo700,
    comingSoon: colors.amber700,
    discontinued: colors.red700,
  },
  iconColor: colors.blueGrey900,
  icon: {
    backgroundColor: 'transparent',
    foregroundColor: 'white',
    internalRatio: 0.66,
    borderRadius: '4%',
    color: colors.grey700,
    disabledColor: '#AAAAAA',
    size: createSizes(8, 12, 20, 20, 38, 68),
    margin: 4,
  },
  action: {
    size: createSizes(12, 24, 36, 36, 48, 60),
  },
  icons: icons,
  animations: animations,
  type: {
    display4: {
      fontStyle: 112,
      fontWeight: 300,
      lineHeight: '128px',
      letterSpacing: -5,
    },
    display3: {
      fontSize: 56,
      fontWeight: 400,
      lineHeight: '64px',
    },
    display2: {
      fontSize: 45,
      fontWeight: 400,
      lineHeight: '64px',
    },
    display1: {
      fontSize: 34,
      fontWeight: 400,
      lineHeight: '52px',
    },
    headline: {
      fontSize: 24,
      fontWeight: 400,
      lineHeight: '44px',
    },
    title: {
      fontSize: 20,
      fontWeight: 600,
      lineHeight: '44px',
    },
    subhead: {
      fontSize: 16,
      fontWeight: 400,
      lineHeight: '32px',
    },
    menu: {
      fontSize: 14,
      fontWeight: 600,
      lineHeight: '32px',
    },
    button: {
      fontSize: 14,
      transform: 'uppercase',
      fontWeight: 600,
      lineHeight: '32px',
    },
    caption: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: '32px',
    },
    body: {
      fontSize: 14,
      fontWeight: 400,
      lineHeight: '25px',
    },
    entityTitle: {
      fontSize: 14,
      fontWeight: 600,
      lineHeight: '25px',
      color: colors.grey700,
    },
    entitySubtitle: {
      fontSize: 12,
      lineHeight: '8px',
      color: colors.grey700,
    },
  },
  components: {
    textField: {
      floatingLabel: {
        color: colors.grey700,
      },
      floatingLabelFocus: {
        color: colors.deepOrange700,
      },
      underlineDisabledStyle: {
        borderBottom: '1px solid rgb(207, 212, 216)',
      },
    },
  },
  views: {
    contextScene: {
      height: 'calc(100% - 132px)',
      width: '100%',
    },
    flexContainer: {
      display: 'flex',
      flexDirection: 'row',
      height: '100%',
      minWidth: minViewWidth,
      width: '100%',
    },
    flexContainerInner: {
      margin: '0 auto',
      minWidth: minViewWidth,
      maxWidth: maxViewWidth,
      width: '80%',
    },
    flexContainerMaster: {
      maxWidth: 384,
      minWidth: 256,
      flex: '0 0 25%',
    },
    flexContainerDetail: {
      flexGrow: 1,
      minWidth: 768,
    },
  },
})

export default muiV1Theme
